import { useTranslation } from "react-i18next";
import { Button } from "@foodpilot/foods";
import { CircularProgress, Stack } from "@mui/material";

import { CreatePageDialog } from "./components/CreatePageDialog";
import { DeleteConfirmDialog } from "./components/DeleteConfirmDialog";
import { Description } from "./components/IconsExport";
import { CMSEditorPagesTable } from "./components/CMSEditorPagesTable";
import { CMSLayout } from "../CMSLayout";
import { useCMSTemplatesManagerController } from "./hooks/useCMSTemplatesManagerController";
import { CREATE, DELETE, PUCK_PAGE, useFoodpilotContext } from "src/context/FoodpilotContext";

export const CMSTemplatesManager = () => {
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const controller = useCMSTemplatesManagerController();
    const canCreatePage = canUser(CREATE, PUCK_PAGE);
    const canDeletePage = canUser(DELETE, PUCK_PAGE);

    const topBar =
        canCreatePage ?
            <Stack direction="row" spacing={2}>
                <Button variant="ternary" startIcon={<Description />} onClick={controller.openCreateDialog}>
                    {t("settings.cms.templates.create", "Create template")}
                </Button>
                {controller.isCreating && <CircularProgress size={24} sx={{ color: "white" }} />}
            </Stack>
        :   undefined;

    return (
        <CMSLayout
            title={t("settings.cms.pdftemplates.title", "PDF templates")}
            isLoading={controller.isLoading}
            isError={controller.isError}
            topBar={topBar}
        >
            <CMSEditorPagesTable
                pages={controller.pages}
                onEdit={controller.navigateToEditor}
                getEditUrl={controller.getEditUrl}
                pageType="PDF"
                onDelete={controller.confirmDelete}
                onUpdateTitle={controller.updateTitle}
                onUpdateRoles={controller.updateRoles}
                onUpdateMeta={controller.updateMeta}
                onUpdateStatus={controller.updateStatus}
                onDuplicatePage={controller.duplicateTemplate}
                pagination={controller.table.pagination}
                setPagination={controller.table.setPagination}
                sorting={controller.table.sorting}
                setSorting={controller.table.setSorting}
                columnFilters={controller.table.columnFilters}
                setColumnFilters={controller.table.setColumnFilters}
                totalItems={controller.totalItems}
                isServerSide={true}
                isCreating={controller.isCreating}
            />

            {canCreatePage && (
                <CreatePageDialog
                    open={controller.isCreateDialogOpen}
                    onClose={controller.closeCreateDialog}
                    onCreatePage={controller.createTemplate}
                    isCreating={controller.isCreating}
                    pageType="PDF"
                />
            )}

            {canDeletePage && (
                <DeleteConfirmDialog
                    open={controller.deleteConfirmDialogOpen}
                    onClose={controller.closeDeleteConfirmDialog}
                    onConfirmDelete={controller.deleteTemplate}
                    isDeleting={controller.actions.isDeleting}
                    pageName={controller.pageToDelete || ""}
                />
            )}
        </CMSLayout>
    );
};
